import React, { useMemo } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Building, Briefcase, LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type EntityType = "client" | "fund";

interface EntityAvatarProps {
  entityType: EntityType;
  name?: string;
  identifier?: string;
  className?: string;
  size?: "sm" | "md" | "lg";
  showLabel?: boolean;
}

/**
 * Generates a deterministic color based on a string
 */
const generateColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash;
  }

  const h = hash % 360;
  const s = 65 + (hash % 20);
  const l = 65 + (hash % 15);

  return `hsl(${h}, ${s}%, ${l}%)`;
};

/**
 * Gets initials from a name (up to 2 characters)
 */
const getInitials = (name: string): string => {
  if (!name) return "";

  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }

  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

export const EntityAvatar: React.FC<EntityAvatarProps> = ({
  entityType,
  name,
  identifier,
  className = "",
  size = "md",
  showLabel = false,
}) => {
  const sizeClasses = {
    sm: "h-6 w-6 text-xs",
    md: "h-8 w-8 text-sm",
    lg: "h-10 w-10 text-base",
  };

  // For funds we use a dynamic color based on identifier
  const bgColor = useMemo(() => {
    if (entityType === "fund" && identifier) {
      return generateColor(identifier);
    }
    return undefined;
  }, [entityType, identifier]);

  // Get initials for fund avatars
  const initials = useMemo(() => {
    if (entityType === "fund") {
      if (name) return getInitials(name);
      return identifier ? identifier.substring(0, 2).toUpperCase() : "";
    }
    return "";
  }, [entityType, name, identifier]);

  // Render client avatar differently
  if (entityType === "client") {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <div className="flex items-center justify-center bg-primary/10 text-primary rounded-full h-8 w-8">
          <Building className="h-4 w-4" />
        </div>

        {showLabel && (
          <div className="flex flex-col items-start overflow-hidden">
            <span className="text-xs text-muted-foreground font-normal">
              Client
            </span>
            <span className="text-sm truncate max-w-[140px] font-medium">
              {name || "Unknown Client"}
            </span>
          </div>
        )}
      </div>
    );
  }

  // Render fund avatar
  return (
    <Avatar className={`rounded-full ${className}`}>
      <AvatarFallback
        className={`${sizeClasses[size]} text-gray-900 rounded-full`}
        style={{ backgroundColor: bgColor }}
      >
        {initials || <Briefcase className="h-4 w-4" />}
      </AvatarFallback>
    </Avatar>
  );
};

export default EntityAvatar;
